
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

export const useAuthFormState = () => {
  const [error, setError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string>("");
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const location = useLocation();
  const errorShownRef = useRef(false);

  useEffect(() => {
    // Skip if already initialized
    if (formInitialized) return;
    
    // Initialize with a small delay to avoid race conditions
    const initTimer = setTimeout(() => {
      try {
        // Helper function to check all possible URL locations for parameters
        const getParamFromAllSources = (paramName: string): string | null => {
          // Check URL search params first
          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams.has(paramName)) {
            return searchParams.get(paramName);
          }
          
          // Check hash params next
          const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
          if (hashParams.has(paramName)) {
            return hashParams.get(paramName);
          }
          
          // Check if param exists in search string (might be differently formatted)
          if (location.search.includes(`${paramName}=`)) {
            const match = location.search.match(new RegExp(`[?&]${paramName}=([^&]*)`));
            return match ? match[1] : null;
          }
          
          // Check if param exists in hash string (might be differently formatted)
          if (location.hash.includes(`${paramName}=`)) {
            const match = location.hash.match(new RegExp(`[#&]${paramName}=([^&]*)`));
            return match ? match[1] : null;
          }
          
          return null;
        };

        // Check for error in all possible URL sources
        const errorParam = getParamFromAllSources('error');
        const errorDescription = getParamFromAllSources('error_description');
        const errorCode = getParamFromAllSources('error_code');

        // Check for recovery type in URL params
        const recoveryType = getParamFromAllSources('type');
        const isRecoveryFlow = recoveryType === 'recovery';

        // Check for magic link token in URL
        const accessToken = getParamFromAllSources('access_token');
        const refreshToken = getParamFromAllSources('refresh_token');
        const type = getParamFromAllSources('type');
        const isMagicLink = accessToken && refreshToken && type === 'recovery';

        // If we're in a recovery flow, show the password reset form
        if (isRecoveryFlow) {
          console.log('Recovery flow detected in URL params, showing password reset form');
          setIsPasswordReset(true);
          
          // Set localStorage flag for password reset awareness 
          localStorage.setItem('passwordResetRequired', 'true');
        }
        
        if (errorParam && !errorShownRef.current) {
          errorShownRef.current = true;
          const message = errorDescription || `Authentication error: ${errorParam}`;
          console.error('Auth error from URL:', {
            message,
            code: errorCode
          });

          // Handle specific error types
          if (errorCode === 'otp_expired') {
            setError("Password reset link has expired. Please request a new one.");
            toast.error("Password reset link has expired. Please request a new one.");
          } else {
            setError(message);
            toast.error(message);
          }

          // Clean up URL without causing a navigation
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        } else if (isMagicLink) {
          // If this is a magic link, we'll let Supabase handle it
          console.log('Magic link detected in URL, handling authentication...');
        }

        // Debug: Log the current URL formats
        console.log('Current URL details:', {
          href: window.location.href,
          origin: window.location.origin,
          pathname: window.location.pathname,
          hostname: window.location.hostname,
          hash: window.location.hash,
          search: window.location.search,
          type: recoveryType,
          isRecoveryFlow
        });
      } catch (e) {
        console.error("Error initializing auth form state:", e);
      } finally {
        setFormInitialized(true);
      }
    }, 300);
    
    return () => clearTimeout(initTimer);
  }, [location, formInitialized]);

  // Toggle between password reset form and login form
  const togglePasswordReset = () => {
    setIsPasswordReset(!isPasswordReset);
    setError(null);
    setEmailSent(false);
  };

  return {
    error,
    setError,
    resetEmail,
    setResetEmail,
    isPasswordReset,
    setIsPasswordReset,
    isLoading,
    setIsLoading,
    emailSent,
    setEmailSent,
    togglePasswordReset,
    formInitialized
  };
};
